import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk } from '../../../store';
import { SelectOption } from '../Annotation/annotation.types';
import axios from '../../../axios';
import { ToastAnt } from '../../Shared/Toast/ToastAnt';

export type EightDTemplate = {
  id?: string;
  code: string;
  description: string;
  canDelete: boolean;
  canUpdate: boolean;
  deprecated: boolean;
};

type EightDTemplateFiltersRequest = {
  codes: string[];
};

type EightDTemplateFiltersResponse = {
  codes: SelectOption[];
};

type PaggingConfiguration = {
  take: number;
  skip: number;
  getTotal: boolean;
  filters: EightDTemplateFiltersRequest;
};

type PaggingResult = {
  totalNumber?: number;
  elements: EightDTemplate[];
  filters: EightDTemplateFiltersResponse;
};

export const TAKE = 10;

type State = {
  eightDTemplates: Array<EightDTemplate>;
  codes: SelectOption[];
  selectedCode?: string;
  loading: boolean;
  formLoading: boolean;
  formResult?: boolean;
  totalNumber: number;
  currentPage: number;
  size: number;
};

const initialState: State = {
  eightDTemplates: [],
  loading: false,
  formLoading: false,
  totalNumber: 0,
  currentPage: 0,
  size: TAKE,
  codes: []
};

const eightDTemplateSlice = createSlice({
  name: 'eightDTemplate',
  initialState,
  reducers: {
    startLoading(state) {
      state.loading = true;
    },
    endLoading(state) {
      state.loading = false;
    },
    startFormLoading(state) {
      state.formLoading = true;
    },
    endFormLoading(state) {
      state.formLoading = false;
    },
    setFormResult(state, action: PayloadAction<boolean | undefined>) {
      state.formResult = action.payload;
    },
    populatePaggingFields(state, action: PayloadAction<PaggingResult>) {
      const { elements, filters, totalNumber } = action.payload;
      state.eightDTemplates = elements;
      state.codes = filters.codes;

      if (totalNumber) {
        state.totalNumber = totalNumber;
      }

      state.loading = false;
    },
    setSizeAndPage: (state, action: PayloadAction<{ size: number; page: number }>) => {
      const { page, size } = action.payload;
      state.size = size;
      state.currentPage = page;
    },
    setSelectedCode(state, action: PayloadAction<string | undefined>) {
      state.selectedCode = action.payload;
    }
  }
});

export const {
  startLoading,
  endLoading,
  startFormLoading,
  endFormLoading,
  setFormResult,
  populatePaggingFields,
  setSizeAndPage,
  setSelectedCode
} = eightDTemplateSlice.actions;

export default eightDTemplateSlice.reducer;

export const getEightDTemplates =
  (take: number, newPage: number): AppThunk =>
  async (dispatch, getState) => {
    const { selectedCode } = getState().eightDTemplate;

    dispatch(startLoading());

    dispatch(setSizeAndPage({ page: newPage, size: take }));

    const config: PaggingConfiguration = {
      take: take,
      skip: (newPage - 1) * take,
      getTotal: true,
      filters: {
        codes: selectedCode ? [selectedCode] : []
      }
    };

    try {
      const response = await axios.post<PaggingResult>('/eightd/search', config);
      dispatch(populatePaggingFields(response.data));
    } catch (error) {
      console.log(error);
    }

    dispatch(endLoading());
  };

export const createEightDTemplate =
  (template: EightDTemplate): AppThunk =>
  async (dispatch, getState) => {
    const { size } = getState().eightDTemplate;

    dispatch(startFormLoading());

    try {
      await axios.post('/eightd', template);

      dispatch(getEightDTemplates(size, 1));
      dispatch(setFormResult(true));
      ToastAnt('Saved.');
    } catch (error) {
      handleError(error);
      dispatch(setFormResult(false));
    }

    dispatch(endFormLoading());
  };

export const updateEightDTemplate =
  (template: EightDTemplate): AppThunk =>
  async (dispatch, getState) => {
    const { size } = getState().eightDTemplate;

    dispatch(startFormLoading());

    try {
      await axios.put('/eightd', template);

      dispatch(getEightDTemplates(size, 1));
      dispatch(setFormResult(true));
      ToastAnt('Saved.');
    } catch (error) {
      dispatch(setFormResult(false));
      handleError(error);
    }

    dispatch(endFormLoading());
  };

export const deleteEightDTemplate =
  (id: string): AppThunk =>
  async (dispatch, getState) => {
    const { size } = getState().eightDTemplate;

    dispatch(startLoading());

    try {
      await axios.delete(`/eightd/${id}`);

      dispatch(getEightDTemplates(size, 1));
      ToastAnt('Deleted.');
    } catch (error) {
      console.log(error);
      ToastAnt('An error occurred.');
    }

    dispatch(endLoading());
  };

function handleError(error: any) {
  console.log(error);
  if (error && error.response && error.response.data && error.response.data.Message) {
    ToastAnt(error.response.data.Message);
  } else {
    ToastAnt('An error occurred.');
  }
}
